import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {HELIX_QUERY_KEY_MAP} from '../api/helix/constant';
import {getHelixSeriesList} from '../api/helix/getHelixSeriesList';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {t} from '../common/getTranslations';
import {ApiStatusCodeVo} from '../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {PagewebError} from '../errors/PagewebError.class';
import {PagewebErrorType} from '../errors/PagewebErrorType.enum';
import {CustomApolloError} from '../graphql/api/CustomApolloError';
import {createHelixSeriesList} from '../graphql/factory/HelixSeriesList';
import type {
  HelixSeriesListQuery,
  HelixSeriesListQueryVariables,
} from '../graphql/generated/types';

/**
 * Helix 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const helixFactory = {
  /**
   * Helix 시리즈 목록 조회
   *
   * ⚠️ Resolver 로직:
   * - mapUid를 Number로 변환
   * - response.result가 없으면 에러 throw
   * - createHelixSeriesList factory로 변환
   */
  seriesList: (
    variables: HelixSeriesListQueryVariables,
    options?: Omit<
      UseQueryOptions<HelixSeriesListQuery, PagewebError, HelixSeriesListQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: HELIX_QUERY_KEY_MAP.seriesList(variables),
      queryFn: async () => {
        const response = await getHelixSeriesList(
          {
            mapUid: Number(variables.mapUid),
          },
          fetcherOptions
        );

        if (!response || !response.result) {
          throw new CustomApolloError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.NicolasApiStatusError,
            resultCode: ApiStatusCodeVo.HelixLandingEmptySeriesList,
            message: t('api_content_not_valid_series_list'),
          });
        }

        // Adapter의 combine 로직을 여기서 수행
        return {
          helixSeriesList: createHelixSeriesList(response.result),
        };
      },
      ...options,
    }),
} as const;
